import { Keypair } from '@solana/web3.js';

const STORAGE_KEY = 'muulti-email-wallet-session';
const DB_NAME = 'muulti-email-wallet-session';
const STORE_NAME = 'unlockedSessions';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function openSessionDatabase() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('Email wallet session storage is blocked.'));
    });
}

async function withSessionStore(mode, action) {
    const database = await openSessionDatabase();
    try {
        return await new Promise((resolve, reject) => {
            const transaction = database.transaction(STORE_NAME, mode);
            const request = action(transaction.objectStore(STORE_NAME));
            transaction.oncomplete = () => resolve(request?.result);
            transaction.onerror = () => reject(transaction.error);
            transaction.onabort = () => reject(transaction.error || new Error('Session storage was interrupted.'));
        });
    } finally {
        database.close();
    }
}

// Only the random handle lives in sessionStorage. Signing material is encrypted
// with a non-extractable CryptoKey in IndexedDB; no password or phrase is stored.
export async function saveEmailWalletSession(keypair, isCurrent = () => true) {
    const crypto = window.crypto;
    const publicKey = keypair.publicKey.toBase58();
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const secret = Uint8Array.from(keypair.secretKey);
    let ciphertext;
    try {
        ciphertext = await crypto.subtle.encrypt({
            name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(publicKey),
        }, key, secret);
    } finally {
        secret.fill(0);
    }
    if (!isCurrent()) return false;
    const id = Array.from(crypto.getRandomValues(new Uint8Array(24)), (byte) => byte.toString(16).padStart(2, '0')).join('');
    const previous = sessionStorage.getItem(STORAGE_KEY);
    await withSessionStore('readwrite', (store) => {
        const expired = store.openCursor();
        expired.onsuccess = () => {
            const cursor = expired.result;
            if (!cursor) return;
            if (cursor.value.expiresAt <= Date.now()) cursor.delete();
            cursor.continue();
        };
        return store.put({ key, iv, ciphertext, publicKey, expiresAt: Date.now() + MAX_AGE_MS }, id);
    });
    try {
        if (!isCurrent()) return false;
        sessionStorage.setItem(STORAGE_KEY, id);
    } finally {
        if (!isCurrent() || sessionStorage.getItem(STORAGE_KEY) !== id) {
            await withSessionStore('readwrite', (store) => store.delete(id));
        }
    }
    if (previous && previous !== id) {
        await withSessionStore('readwrite', (store) => store.delete(previous));
    }
    return true;
}

export async function clearEmailWalletSession() {
    const id = sessionStorage.getItem(STORAGE_KEY);
    // Invalidate the handle synchronously, before waiting for IndexedDB.
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } finally {
        // Revoke the encrypted signing key even if handle removal is blocked.
        if (id) await withSessionStore('readwrite', (store) => store.delete(id));
    }
}

export async function restoreEmailWalletSession() {
    let id;
    try {
        id = sessionStorage.getItem(STORAGE_KEY);
        if (!id) return null;
        const record = await withSessionStore('readonly', (store) => store.get(id));
        if (!record || !Number.isFinite(record.expiresAt) || record.expiresAt <= Date.now()) {
            throw new Error('Email wallet session expired.');
        }
        const plaintext = new Uint8Array(await window.crypto.subtle.decrypt({
            name: 'AES-GCM', iv: record.iv, additionalData: new TextEncoder().encode(record.publicKey),
        }, record.key, record.ciphertext));
        try {
            const keypair = Keypair.fromSecretKey(Uint8Array.from(plaintext));
            if (keypair.publicKey.toBase58() !== record.publicKey) {
                throw new Error('Email wallet session identity mismatch.');
            }
            // Disconnect or a newer login may have invalidated this lookup.
            return sessionStorage.getItem(STORAGE_KEY) === id ? keypair : null;
        } finally {
            plaintext.fill(0);
        }
    } catch {
        // An unavailable, expired, or corrupt cache never authenticates a wallet.
        try {
            if (id && sessionStorage.getItem(STORAGE_KEY) === id) {
                await clearEmailWalletSession();
            }
        } catch {}
        return null;
    }
}
